import React, { useState } from "react";
import SocialContainer from "./SocialContainer";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  FaWhatsapp,
  FaEnvelope,
  FaCheckCircle,
  FaArrowRight,
  FaSignInAlt,
} from "react-icons/fa";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom"; // Import useNavigate for redirection

const EmailUser = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate(); // Initialize navigate for redirection

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setEmailExists(false);

    // Simple email validation
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_ENDPOINT}/api/email-verification`,
        { email },
        getHeader()
      );
      const responseD = response.data;

      if (responseD.exists) {
        setEmailExists(true);
        toast.info("Email already exists. Please login instead.");
      } else {
        setIsSubmitted(true);
        toast.success(responseD.message);
      }
    } catch (error) {
      setError(getErrorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate("/login"); // Replace with your actual login route
  };

  return (
    <SocialContainer>
      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1, py: 4 }}>
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 3,
            background: "white",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
          }}
        >
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <FaWhatsapp
                style={{
                  color: "#25D366",
                  fontSize: "2.5rem",
                  marginRight: "10px",
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "700",
                  background:
                    "linear-gradient(135deg, #1976d2 0%, #25D366 100%)",
                  backgroundClip: "text",
                  textFillColor: "transparent",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                WhatsApp Business API
              </Typography>
            </Box>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
              Connect with your customers at scale
            </Typography>

            {emailExists ? (
              <>
                <Box sx={{ mb: 3 }}>
                  <FaEnvelope
                    style={{ fontSize: 48, color: "#1976d2", marginBottom: 2 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Email Already Registered
                  </Typography>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    sx={{ mb: 3 }}
                  >
                    The email <strong>{email}</strong> is already associated
                    with an account. Please login to access your WhatsApp
                    Business API dashboard.
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<FaSignInAlt />}
                    onClick={handleLoginRedirect}
                    sx={{
                      background:
                        "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                      },
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: "600",
                      minWidth: 200,
                    }}
                  >
                    Login
                  </Button>
                </Box>

                <Typography variant="body2" color="textSecondary">
                  Not you?{" "}
                  <Button
                    color="primary"
                    sx={{ textTransform: "none", fontWeight: 600 }}
                    onClick={() => {
                      setEmailExists(false);
                      setEmail("");
                    }}
                  >
                    Use a different email
                  </Button>
                </Typography>
              </>
            ) : !isSubmitted ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                  }}
                >
                  <FaEnvelope style={{ marginRight: 10, color: "#1976d2" }} />
                  <Typography variant="h6" color="primary" fontWeight="600">
                    Verify Your Email
                  </Typography>
                </Box>

                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{ mb: 3 }}
                >
                  Enter your email to begin the onboarding process. We'll send
                  you a verification link to get started.
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AlternateEmailIcon />
                        </InputAdornment>
                      ),
                    }}
                    type="email"
                    label="Email Address"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{ mb: 3, maxWidth: 480 }}
                    required
                    variant="outlined"
                    disabled={isLoading}
                  />
                  <Box>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      endIcon={!isLoading && <FaArrowRight />}
                      disabled={isLoading}
                      sx={{
                        background:
                          "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #128C7E 0%, #075E54 100%)",
                        },
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: "600",
                        minWidth: 200,
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} sx={{ color: "white" }} />
                      ) : (
                        "Continue"
                      )}
                    </Button>
                  </Box>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ mb: 3 }}>
                  <FaCheckCircle
                    style={{ fontSize: 64, color: "#4CAF50", marginBottom: 2 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Verification Email Sent!
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    We've sent a verification link to <strong>{email}</strong>.
                    Check your inbox and click the link to continue your
                    WhatsApp Business API setup.
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontStyle: "italic" }}
                >
                  Didn't receive the email? Check your spam folder or{" "}
                  <Button
                    color="primary"
                    sx={{ textTransform: "none", fontWeight: 600 }}
                    onClick={() => setIsSubmitted(false)}
                  >
                    try again
                  </Button>
                  .
                </Typography>
              </>
            )}

            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 3, display: "block" }}
            >
              By continuing, you agree to our{" "}
              <a
                href="https://www.smartgrowthai.com/terms"
                color="primary"
                underline="hover"
                target="_blank"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="https://www.smartgrowthai.com/privacypolicy"
                color="primary"
                underline="hover"
                target="_blank"
              >
                Privacy Policy
              </a>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </SocialContainer>
  );
};

export default EmailUser;
